import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container, Box, Typography } from '@mui/material';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './accessibilita.scss';

gsap.registerPlugin(ScrollTrigger);

function Accessibilita() {
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const animateSplitText = (element: HTMLElement) => {
    const text = element.textContent || '';
    element.innerHTML = text
      .split('')
      .map(char => char === ' ' ? ' ' : `<span class="split-char">${char}</span>`)
      .join('');

    const chars = element.querySelectorAll('.split-char');
    gsap.fromTo(chars,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.03,
        ease: "power3.out",
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          toggleActions: "play none none none"
        }
      }
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (heroRef.current) {
        gsap.fromTo(heroRef.current,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 1, ease: "power2.out" }
        );
      }
      if (titleRef.current) {
        animateSplitText(titleRef.current);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Accessibilità | Fabbri Agricoltura</title>
        <meta name="description" content="Dichiarazione di accessibilità del sito Fabbri Agricoltura ai sensi della Legge 4/2004 e delle Linee Guida AgID." />
      </Helmet>

      <Box className="legal-page-agricola" id="top">
        {/* Hero */}
        <Box className="legal-hero-agricola" ref={heroRef}>
          <Container maxWidth="lg" className="legal-hero-content">
            <Typography variant="h1" className="legal-hero-title split-text" ref={titleRef}>
              Dichiarazione di Accessibilità
            </Typography>
            <Typography className="legal-hero-subtitle">
              Ai sensi della Legge 4/2004 e delle Linee Guida AgID sull'accessibilità dei siti web.
            </Typography>
          </Container>
        </Box>

        {/* Content */}
        <Box className="legal-content-section">
          <Container maxWidth="lg">

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">Stato di conformità</Typography>
              <Typography className="legal-text">
                Il sito <strong>fabbriagricoltura.it</strong> è parzialmente conforme ai requisiti previsti dalla norma UNI EN 301549 e dalle WCAG 2.1 livello AA, a causa delle limitazioni elencate di seguito.
              </Typography>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">Contenuti non accessibili</Typography>
              <Typography className="legal-text">
                I seguenti contenuti non sono ancora pienamente accessibili:
              </Typography>
              <ul className="legal-list">
                <li>Alcune immagini di prodotto potrebbero non avere un testo alternativo sufficientemente descrittivo</li>
                <li>La navigazione da tastiera potrebbe non essere ottimale in tutti i componenti interattivi</li>
                <li>I video decorativi nelle sezioni hero non dispongono di sottotitoli o trascrizioni (sono video ambientali senza dialoghi)</li>
              </ul>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">Misure adottate</Typography>
              <Typography className="legal-text">
                Per migliorare l'accessibilità del sito sono state adottate le seguenti misure:
              </Typography>
              <ul className="legal-list">
                <li>Utilizzo di HTML semantico e struttura corretta dei titoli</li>
                <li>Contrasto dei colori conforme alle linee guida WCAG 2.1 AA</li>
                <li>Attributi ARIA dove necessario per componenti interattivi</li>
                <li>Layout responsive adattabile a diverse dimensioni di schermo</li>
                <li>Testi alternativi per le immagini principali, con indicazione esplicita dei contenuti generati con intelligenza artificiale (ai sensi dell'art. 50 Reg. UE 2024/1689)</li>
                <li>Attributi aria-label sui video generati con IA per informare gli utenti di tecnologie assistive</li>
                <li>Form di contatto con label e validazione accessibili</li>
              </ul>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">Feedback e segnalazioni</Typography>
              <Typography className="legal-text">
                Se riscontri problemi di accessibilità su questo sito o hai bisogno di contenuti in un formato alternativo, contattaci:
              </Typography>
              <ul className="legal-list">
                <li>Email: <a href="mailto:fabbri.agricoltura@outlook.it">fabbri.agricoltura@outlook.it</a></li>
                <li>Telefono: <a href="tel:+393200441982">+39 3200441982</a></li>
              </ul>
              <Typography className="legal-text">
                Cercheremo di rispondere entro 5 giorni lavorativi.
              </Typography>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">Procedura di attuazione</Typography>
              <Typography className="legal-text">
                In caso di risposta insoddisfacente o mancata risposta entro 30 giorni, è possibile inviare una segnalazione al Difensore civico per il digitale presso l'AgID (Agenzia per l'Italia Digitale) tramite il{' '}
                <a href="https://www.agid.gov.it/" target="_blank" rel="noopener noreferrer">sito di AgID</a>.
              </Typography>
            </Box>

            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">Riferimenti normativi</Typography>
              <ul className="legal-list">
                <li>Legge 9 gennaio 2004, n. 4 (Legge Stanca)</li>
                <li>D.Lgs. 10 agosto 2018, n. 106</li>
                <li>Linee Guida AgID sull'accessibilità</li>
                <li>WCAG 2.1 - Web Content Accessibility Guidelines</li>
                <li>UNI EN 301549</li>
              </ul>
            </Box>

            <Box className="legal-block legal-footer-info">
              <Typography className="legal-text">
                <strong>Fabbri Agricoltura</strong><br />
                <a href="https://maps.app.goo.gl/ws26E8mfGKWDVsZL9" target="_blank" rel="noopener noreferrer">Via Per S. Felice, 3 - 41031 Camposanto (MO)</a><br />
                P.IVA 04004150365
              </Typography>
              <Typography className="legal-text legal-update">
                Ultimo aggiornamento: agosto 2026
              </Typography>
            </Box>

          </Container>
        </Box>
      </Box>
    </>
  );
}

export default Accessibilita;
